/**
 * The expression language, on the client.
 *
 * A port of `twasta_runtime_engine/expr_eval.py` — the SAME language every
 * item_change fill and validation rule in the platform is written in (D8/D26),
 * not a second one. It exists because a document form's calculations have to
 * recompute as somebody types: a round trip per keystroke is not a spreadsheet,
 * and a total that only appears after Save is not a document anybody trusts.
 *
 * **The server still decides.** Whatever this computes, the engine recomputes
 * the same expressions from the posted tree at save and uses ITS results
 * (§6i) — a client value that disagrees is logged as drift, and above
 * `governance: "none"` it is refused. This is the preview; that is the record.
 *
 * **Parity is a test, not an intention.** `test-script/fixtures/expr/cases.json`
 * is the definition of both implementations; `test_expr_eval_parity.py` and
 * `test_expr_eval_parity.mjs` run every case through their own side. A helper
 * added here and not there — or there and not here — fails. Two evaluators of
 * one language stay one language only while something enforces it.
 *
 * Syntax (a safe subset of Python, exactly as the engine accepts it):
 *   - numbers, single/double-quoted strings, True/False
 *   - + - * / // % **, comparisons, `and` / `or` / `not`, parentheses
 *   - `a if cond else b`, `x in ["A", "B"]`
 *   - whitelisted function calls only — no attribute access, no subscripting,
 *     no comprehensions, no assignment, no name that was not supplied
 *   - SQL spellings (`AND`, `OR`, `NOT`, `<>`, a lone `=`) normalised, outside
 *     string literals only
 */

export class ExprError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ExprError'
  }
}

/**
 * Many values under one name — a repeating form's column (§6i).
 *
 * Arithmetic and comparison broadcast over it; the aggregates reduce it; a
 * context that can only mean one value refuses it. The mirror of
 * `expr_eval.Vector`, and for the same reasons.
 */
export class Vector extends Array<unknown> {
  static of2(items: unknown[]): Vector {
    const out = new Vector()
    for (const item of items) out.push(item)
    return out
  }
}

/**
 * A Python `int` literal.
 *
 * The one place the two languages genuinely differ: everything the engine's
 * evaluator computes is a `float`, so `str()` of it reads "2.0", while a bare
 * int literal reads "2". JavaScript has one number type and would print "2"
 * for both, so `concat(2, "x")` and `concat(1 + 1, "x")` would agree with the
 * server on one and not the other. Boxing the literal — and only the literal,
 * since every helper returns a float — makes the two agree exactly.
 */
class PyInt {
  constructor(readonly value: number) {}
}

const INT_RE = /^[+-]?\d+$/

// ── coercion ────────────────────────────────────────────────────────────────

const MONTH_DAYS = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334]

function isLeap(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0
}

function daysInMonth(y: number, m: number): number {
  if (m === 2) return isLeap(y) ? 29 : 28
  return [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1] ?? 0
}

/** Python `date.toordinal()` — day 1 is 0001-01-01, proleptic Gregorian. */
function toOrdinal(y: number, m: number, d: number): number {
  const y1 = y - 1
  let days = y1 * 365 + Math.floor(y1 / 4) - Math.floor(y1 / 100)
    + Math.floor(y1 / 400)
  days += MONTH_DAYS[m - 1]
  if (m > 2 && isLeap(y)) days += 1
  return days + d
}

const DATE_PATTERNS: [RegExp, 0 | 2][] = [
  // [pattern, index of the YEAR group] — %Y first, then %d first, exactly the
  // order `_NUM_DATE_FORMATS` tries them in.
  [/^(\d{4})-(\d{1,2})-(\d{1,2})$/, 0],
  [/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/, 0],
  [/^(\d{1,2})-(\d{1,2})-(\d{4})$/, 2],
  [/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, 2],
]

/** The day-ordinal of a date-ish string, or null. A time component is ignored. */
export function dateOrdinal(v: unknown): number | null {
  if (v instanceof Date) {
    return toOrdinal(v.getUTCFullYear(), v.getUTCMonth() + 1, v.getUTCDate())
  }
  if (typeof v !== 'string') return null
  const head = v.trim().replace('T', ' ').split(' ')[0]
  if (!head) return null
  for (const [pattern, yearAt] of DATE_PATTERNS) {
    const match = pattern.exec(head)
    if (!match) continue
    const nums = [Number(match[1]), Number(match[2]), Number(match[3])]
    const [y, m, d] = yearAt === 0
      ? [nums[0], nums[1], nums[2]]
      : [nums[2], nums[1], nums[0]]
    // strptime rejects an impossible date rather than rolling it over, and so
    // must this: "2024-02-31" is not a date, it is a typo.
    if (m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) continue
    return toOrdinal(y, m, d)
  }
  return null
}

/** Python `float(...)` syntax — deliberately NOT `Number()`, which takes hex. */
const FLOAT_RE = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/
const INF_NAN_RE = /^[+-]?(inf|infinity|nan)$/i

export function num(v: unknown): number {
  if (v instanceof PyInt) return v.value
  if (v === null || v === undefined || v === '') return 0
  if (typeof v === 'boolean') return v ? 1 : 0
  if (typeof v === 'number') return v
  if (v instanceof Date) {
    return toOrdinal(v.getUTCFullYear(), v.getUTCMonth() + 1, v.getUTCDate())
  }
  const s = String(v).trim()
  if (FLOAT_RE.test(s)) return Number(s)
  if (INF_NAN_RE.test(s)) {
    const neg = s.startsWith('-')
    if (/nan/i.test(s)) return NaN
    return neg ? -Infinity : Infinity
  }
  const ord = dateOrdinal(s)
  return ord === null ? 0 : ord
}

/** Python `str(...)` — see `PyInt` for why a float prints its `.0`. */
export function str(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (v instanceof PyInt) return String(v.value)
  if (typeof v === 'boolean') return v ? 'True' : 'False'
  if (typeof v === 'number') {
    if (Number.isNaN(v)) return 'nan'
    if (v === Infinity) return 'inf'
    if (v === -Infinity) return '-inf'
    // Every number the evaluator itself produces is a Python float.
    if (Number.isInteger(v) && Math.abs(v) < 1e16) return `${v}.0`
    return String(v)
  }
  return String(v)
}

function isNumericish(v: unknown): boolean {
  if (v === null || v === undefined || typeof v === 'boolean'
    || v instanceof PyInt) return true
  if (typeof v === 'number' || v instanceof Date) return true
  if (typeof v !== 'string') return false
  const s = v.trim()
  if (s === '') return true
  if (FLOAT_RE.test(s) || INF_NAN_RE.test(s)) return true
  return dateOrdinal(s) !== null
}

function cmpStr(v: unknown): string {
  return str(v).trim().toLowerCase()
}

export function truthy(v: unknown): boolean {
  if (v instanceof Vector) {
    throw new ExprError(
      'A column of a repeating form is many values, so it cannot be used as a '
      + 'single yes/no. Reduce it first — count(…), sum(…), first(…) — or move '
      + 'the expression inside the repeat.')
  }
  if (v instanceof PyInt) return v.value !== 0
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0
  if (typeof v === 'string') {
    const s = v.trim()
    if (s === '') return false
    if (isNumericish(s)) return num(s) !== 0
    return true
  }
  return num(v) !== 0
}

// ── vectors ─────────────────────────────────────────────────────────────────

function vectorLen(args: unknown[]): number | null {
  const lengths = new Set<number>()
  for (const a of args) if (a instanceof Vector) lengths.add(a.length)
  if (!lengths.size) return null
  if (lengths.size > 1) {
    throw new ExprError(
      'This mixes columns from two different repeating forms, which have '
      + 'different numbers of rows. Reduce one of them first — sum(…), '
      + 'first(…), count(…).')
  }
  return [...lengths][0]
}

function broadcast(fn: (...a: unknown[]) => unknown, ...args: unknown[]): unknown {
  const size = vectorLen(args)
  if (size === null) return fn(...args)
  const out: unknown[] = []
  for (let i = 0; i < size; i += 1) {
    out.push(fn(...args.map((a) => (a instanceof Vector ? a[i] : a))))
  }
  return Vector.of2(out)
}

function flatten(args: unknown[]): unknown[] {
  const out: unknown[] = []
  for (const a of args) {
    if (a instanceof Vector) out.push(...a)
    else out.push(a)
  }
  return out
}

function pairs(values: unknown, condition: unknown): [unknown, unknown][] {
  const size = vectorLen([values, condition])
  if (size === null) return [[values, condition]]
  const out: [unknown, unknown][] = []
  for (let i = 0; i < size; i += 1) {
    out.push([
      values instanceof Vector ? values[i] : values,
      condition instanceof Vector ? condition[i] : condition,
    ])
  }
  return out
}

const filled = (v: unknown) => v !== null && v !== undefined && str(v).trim() !== ''

// ── helper functions ────────────────────────────────────────────────────────

/**
 * `round()`, as Python does it — which is not as JavaScript does it.
 *
 * Two differences, and both of them are a cent:
 *
 * 1. **Half rounds to EVEN.** `round(2.5)` is 2 and `round(0.125, 2)` is 0.12.
 *    `Math.round` rounds half up and `toFixed` rounds half away from zero.
 * 2. **The tie is decided on the EXACT binary value, never on a tolerance.**
 *    `2.345` is not really 2.345 — the nearest double is a hair ABOVE it — so
 *    `round(2.345, 2)` is 2.35 and not a tie at all. Treating anything within
 *    an epsilon of .5 as a tie invents ties that are not there and rounds them
 *    the wrong way.
 *
 * `toFixed` is correctly rounded against the exact value, so it already gives
 * the right answer for everything EXCEPT a genuine tie; a genuine tie is one
 * where the exact expansion is `…5` followed by nothing but zeros, which is
 * what the long expansion below is read for.
 *
 * Worth the care: a rounding rule that differs between the screen and the
 * engine's save-time recompute would report drift on every invoice total, and a
 * drift warning nobody can act on is a drift warning everybody ignores.
 */
function pyRound(...args: unknown[]): number {
  if (!args.length) throw new ExprError('round() needs at least one argument.')
  const x = num(args[0])
  const nd = args.length === 1 ? 0 : Math.trunc(num(args[1]))
  if (!Number.isFinite(x)) return x
  if (nd < 0 || nd > 17 || Math.abs(x) >= 1e17) {
    // Outside what a decimal expansion can settle. Scale-and-round is the best
    // available and the difference is far below anything a document displays.
    const factor = 10 ** nd
    return Math.round(x * factor) / factor
  }

  const negative = x < 0
  const magnitude = Math.abs(x)
  // 20 digits is the most `toFixed` allows and far more than a double carries,
  // so anything beyond the tie digit that is not zero settles the question.
  const exact = magnitude.toFixed(20)
  const dot = exact.indexOf('.')
  const decimals = exact.slice(dot + 1)
  const tieDigit = decimals[nd]
  const rest = decimals.slice(nd + 1)
  const isTie = tieDigit === '5' && /^0*$/.test(rest)

  if (!isTie) {
    const rounded = Number(magnitude.toFixed(nd))
    return negative ? -rounded : rounded
  }
  // A real tie: take the truncated value and step up only when that leaves an
  // odd last digit.
  const kept = Number(`${exact.slice(0, dot)}.${decimals.slice(0, nd)}` || '0')
  const factor = 10 ** nd
  const scaled = Math.round(kept * factor)
  const rounded = (scaled % 2 === 0 ? scaled : scaled + 1) / factor
  return negative ? -rounded : rounded
}

function minmax(pick: (a: number, b: number) => number, name: string) {
  return (...args: unknown[]): number => {
    if (!args.length) throw new ExprError(`${name}() needs at least one argument.`)
    const items = (args.length === 1 && args[0] instanceof Vector)
      ? [...(args[0] as Vector)]
      : flatten(args)
    if (!items.length) return 0
    return items.map(num).reduce(pick)
  }
}

function reMatch(s: unknown, pattern: unknown): boolean {
  const pat = str(pattern).slice(0, 200)
  const txt = str(s).slice(0, 2000)
  try {
    return new RegExp(pat).test(txt)
  } catch (exc) {
    throw new ExprError(`Invalid regex pattern: ${(exc as Error).message}`)
  }
}

export const SAFE_FUNCS: Record<string, (...a: unknown[]) => unknown> = {
  // numbers
  round: pyRound,
  abs: (x) => Math.abs(num(x)),
  min: minmax((a, b) => Math.min(a, b), 'min'),
  max: minmax((a, b) => Math.max(a, b), 'max'),
  floor: (x) => Math.floor(num(x)),
  ceil: (x) => Math.ceil(num(x)),
  sqrt: (x) => {
    const n = num(x)
    if (n < 0) throw new ExprError('sqrt() of a negative number.')
    return Math.sqrt(n)
  },
  pow: (x, y) => num(x) ** num(y),
  int: (x) => Math.trunc(num(x)),
  mod: (a, b) => safeMod(a, b),
  // logic / null
  iif: (c, a, b) => (truthy(c) ? a : b),
  coalesce: (...args) => {
    for (const a of args) if (filled(a)) return a
    return ''
  },
  is_empty: (x) => !filled(x),
  not_empty: (x) => filled(x),
  in_list: (x, csv) => {
    const items = new Set(str(csv).split(',').map((p) => p.trim().toLowerCase())
      .filter(Boolean))
    return items.has(cmpStr(x))
  },
  // strings
  len: (x) => str(x).length,
  lower: (x) => str(x).toLowerCase(),
  upper: (x) => str(x).toUpperCase(),
  trim: (x) => str(x).trim(),
  concat: (...args) => args.map(str).join(''),
  contains: (s, sub) => cmpStr(s).includes(cmpStr(sub)),
  startswith: (s, p) => cmpStr(s).startsWith(cmpStr(p)),
  endswith: (s, p) => cmpStr(s).endsWith(cmpStr(p)),
  match: reMatch,
  // over a repeat (§6i)
  sum: (...args) => flatten(args).reduce<number>((t, a) => t + num(a), 0),
  count: (...args) => flatten(args).filter(filled).length,
  avg: (...args) => {
    const items = flatten(args).filter(filled)
    // Blank, not zero: "the average of nothing is zero" is a statement about
    // money that is not true.
    if (!items.length) return ''
    return items.reduce<number>((t, a) => t + num(a), 0) / items.length
  },
  first: (...args) => { const i = flatten(args); return i.length ? i[0] : '' },
  last: (...args) => { const i = flatten(args); return i.length ? i[i.length - 1] : '' },
  sumif: (values, condition) => pairs(values, condition)
    .reduce<number>((t, [v, keep]) => (truthy(keep) ? t + num(v) : t), 0),
  countif: (values, condition) => {
    const cond = condition === undefined ? values : condition
    return pairs(values, cond).filter(([, keep]) => truthy(keep)).length
  },
}

/** Reduces rather than broadcasts — the mirror of `expr_eval.AGGREGATE_FUNCS`. */
export const AGGREGATE_FUNCS = new Set([
  'sum', 'count', 'avg', 'first', 'last', 'sumif', 'countif', 'min', 'max',
])

function safeDiv(a: unknown, b: unknown): number {
  const d = num(b)
  if (d === 0) throw new ExprError('Division by zero.')
  return num(a) / d
}

function safeFloorDiv(a: unknown, b: unknown): number {
  const d = num(b)
  if (d === 0) throw new ExprError('Division by zero.')
  return Math.floor(num(a) / d)
}

function safeMod(a: unknown, b: unknown): number {
  const d = num(b)
  if (d === 0) throw new ExprError('Modulo by zero.')
  // Python's `%` takes the sign of the DIVISOR; JavaScript's takes the sign of
  // the dividend. `-1 % 12` is 11 there and -1 here.
  const n = num(a)
  return ((n % d) + d) % d
}

function compare(tok: string, a: unknown, b: unknown): boolean {
  if (isNumericish(a) && isNumericish(b)) {
    const [x, y] = [num(a), num(b)]
    switch (tok) {
      case 'lt': return x < y
      case 'le': return x <= y
      case 'gt': return x > y
      case 'ge': return x >= y
      case 'eq': return x === y
      default: return x !== y
    }
  }
  const [x, y] = [cmpStr(a), cmpStr(b)]
  switch (tok) {
    case 'lt': return x < y
    case 'le': return x <= y
    case 'gt': return x > y
    case 'ge': return x >= y
    case 'eq': return x === y
    default: return x !== y
  }
}

// ── SQL-spelling normalisation ──────────────────────────────────────────────

const STRING_LITERAL_RE = /'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"/g

function normalizeSegment(seg: string): string {
  return seg
    .replace(/<>/g, '!=')
    .replace(/(?<![<>=!])=(?!=)/g, '==')
    .replace(/\b(and|or|not)\b/gi, (m) => m.toLowerCase())
    .replace(/\b(true|false)\b/gi,
      (m) => m[0].toUpperCase() + m.slice(1).toLowerCase())
}

function normalizeOperators(src: string): string {
  let out = ''
  let last = 0
  for (const m of src.matchAll(STRING_LITERAL_RE)) {
    out += normalizeSegment(src.slice(last, m.index))
    out += m[0]
    last = (m.index ?? 0) + m[0].length
  }
  out += normalizeSegment(src.slice(last))
  return out
}

// ── tokenizer ───────────────────────────────────────────────────────────────

type TokenType = 'num' | 'str' | 'name' | 'op' | 'end'
interface Token { type: TokenType; value: string; isInt?: boolean }

const OPERATORS = [
  '**', '//', '<=', '>=', '==', '!=', '<', '>', '+', '-', '*', '/', '%',
  '(', ')', ',', '[', ']',
]

function tokenize(src: string): Token[] {
  const out: Token[] = []
  let i = 0
  while (i < src.length) {
    const ch = src[i]
    if (/\s/.test(ch)) { i += 1; continue }
    if (ch === "'" || ch === '"') {
      let j = i + 1
      let text = ''
      while (j < src.length && src[j] !== ch) {
        if (src[j] === '\\' && j + 1 < src.length) {
          const next = src[j + 1]
          text += next === 'n' ? '\n' : next === 't' ? '\t' : next
          j += 2
          continue
        }
        text += src[j]
        j += 1
      }
      if (j >= src.length) throw new ExprError('Invalid expression syntax: unterminated string')
      out.push({ type: 'str', value: text })
      i = j + 1
      continue
    }
    if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(src[i + 1] || ''))) {
      const m = /^(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?/.exec(src.slice(i))
      if (!m) throw new ExprError('Invalid expression syntax: number')
      out.push({ type: 'num', value: m[0], isInt: INT_RE.test(m[0]) })
      i += m[0].length
      continue
    }
    if (/[A-Za-z_]/.test(ch)) {
      const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i))!
      out.push({ type: 'name', value: m[0] })
      i += m[0].length
      continue
    }
    const op = OPERATORS.find((o) => src.startsWith(o, i))
    if (!op) {
      throw new ExprError(`Invalid expression syntax: unexpected '${ch}'`)
    }
    out.push({ type: 'op', value: op })
    i += op.length
  }
  out.push({ type: 'end', value: '' })
  return out
}

// ── parser (recursive descent, Python's precedence) ─────────────────────────

type Node =
  | { k: 'const'; v: unknown }
  | { k: 'name'; v: string }
  | { k: 'call'; fn: string; args: Node[] }
  | { k: 'unary'; op: string; a: Node }
  | { k: 'bin'; op: string; a: Node; b: Node }
  | { k: 'cmp'; op: string; a: Node; b: Node }
  | { k: 'in'; neg: boolean; a: Node; items: Node[] }
  | { k: 'bool'; op: 'and' | 'or'; parts: Node[] }
  | { k: 'if'; test: Node; body: Node; orelse: Node }
  | { k: 'list'; items: Node[] }

class Parser {
  private at = 0

  constructor(private readonly tokens: Token[]) {}

  private peek(): Token { return this.tokens[this.at] }

  private next(): Token { return this.tokens[this.at++] }

  private eat(value: string): boolean {
    const t = this.peek()
    if ((t.type === 'op' || t.type === 'name') && t.value === value) {
      this.at += 1
      return true
    }
    return false
  }

  private expect(value: string): void {
    if (!this.eat(value)) {
      throw new ExprError(`Invalid expression syntax: expected ${value}`)
    }
  }

  parse(): Node {
    const node = this.ternary()
    if (this.peek().type !== 'end') {
      throw new ExprError('Invalid expression syntax: unexpected trailing input')
    }
    return node
  }

  /** `A if C else B` — lowest precedence, exactly as in Python. */
  private ternary(): Node {
    const body = this.orExpr()
    if (this.eat('if')) {
      const test = this.orExpr()
      this.expect('else')
      return { k: 'if', test, body, orelse: this.ternary() }
    }
    return body
  }

  private orExpr(): Node {
    const parts = [this.andExpr()]
    while (this.eat('or')) parts.push(this.andExpr())
    return parts.length === 1 ? parts[0] : { k: 'bool', op: 'or', parts }
  }

  private andExpr(): Node {
    const parts = [this.notExpr()]
    while (this.eat('and')) parts.push(this.notExpr())
    return parts.length === 1 ? parts[0] : { k: 'bool', op: 'and', parts }
  }

  private notExpr(): Node {
    if (this.eat('not')) return { k: 'unary', op: 'not', a: this.notExpr() }
    return this.comparison()
  }

  private comparison(): Node {
    const a = this.additive()
    const t = this.peek()
    const CMP: Record<string, string> = {
      '<': 'lt', '<=': 'le', '>': 'gt', '>=': 'ge', '==': 'eq', '!=': 'ne',
    }
    if (t.type === 'op' && CMP[t.value]) {
      this.next()
      const b = this.additive()
      if (this.peek().type === 'op' && CMP[this.peek().value]) {
        throw new ExprError('Chained comparisons are not allowed.')
      }
      return { k: 'cmp', op: CMP[t.value], a, b }
    }
    if (t.type === 'name' && (t.value === 'in' || t.value === 'not')) {
      const save = this.at
      let neg = false
      if (this.eat('not')) {
        neg = true
        if (!this.eat('in')) { this.at = save; return a }
      } else {
        this.expect('in')
      }
      if (!this.eat('[') && !this.eat('(')) {
        throw new ExprError(
          "'in' requires a list or tuple literal on the right side.")
      }
      const items = this.items(']', ')')
      return { k: 'in', neg, a, items }
    }
    return a
  }

  private additive(): Node {
    let a = this.multiplicative()
    for (;;) {
      const t = this.peek()
      if (t.type === 'op' && (t.value === '+' || t.value === '-')) {
        this.next()
        a = { k: 'bin', op: t.value, a, b: this.multiplicative() }
        continue
      }
      return a
    }
  }

  private multiplicative(): Node {
    let a = this.unary()
    for (;;) {
      const t = this.peek()
      if (t.type === 'op'
        && (t.value === '*' || t.value === '/' || t.value === '//'
          || t.value === '%')) {
        this.next()
        a = { k: 'bin', op: t.value, a, b: this.unary() }
        continue
      }
      return a
    }
  }

  private unary(): Node {
    const t = this.peek()
    if (t.type === 'op' && (t.value === '-' || t.value === '+')) {
      this.next()
      return { k: 'unary', op: t.value, a: this.unary() }
    }
    return this.power()
  }

  /** `**` binds tighter than unary minus on the LEFT and looser on the right. */
  private power(): Node {
    const a = this.primary()
    if (this.peek().type === 'op' && this.peek().value === '**') {
      this.next()
      return { k: 'bin', op: '**', a, b: this.unary() }
    }
    return a
  }

  private items(...closers: string[]): Node[] {
    const out: Node[] = []
    if (closers.some((c) => this.eat(c))) return out
    for (;;) {
      out.push(this.ternary())
      if (this.eat(',')) {
        if (closers.some((c) => this.eat(c))) return out
        continue
      }
      if (closers.some((c) => this.eat(c))) return out
      throw new ExprError('Invalid expression syntax: expected , or a closer')
    }
  }

  private primary(): Node {
    const t = this.next()
    if (t.type === 'num') {
      return { k: 'const', v: t.isInt ? new PyInt(Number(t.value)) : Number(t.value) }
    }
    if (t.type === 'str') return { k: 'const', v: t.value }
    if (t.type === 'name') {
      if (t.value === 'True') return { k: 'const', v: true }
      if (t.value === 'False') return { k: 'const', v: false }
      if (this.eat('(')) return { k: 'call', fn: t.value, args: this.items(')') }
      return { k: 'name', v: t.value }
    }
    if (t.type === 'op' && t.value === '(') {
      const inner = this.ternary()
      this.expect(')')
      return inner
    }
    if (t.type === 'op' && t.value === '[') {
      return { k: 'list', items: this.items(']') }
    }
    throw new ExprError(
      `Invalid expression syntax: unexpected ${t.value || 'end of expression'}`)
  }
}

const MAX_LEN = 1000
const MAX_DEPTH = 40

const parseCache = new Map<string, Node>()

function compile(src: string): Node {
  const cached = parseCache.get(src)
  if (cached) return cached
  const node = new Parser(tokenize(normalizeOperators(src))).parse()
  // Bounded, like the engine's `lru_cache(1024)`: a project with very many
  // distinct expressions must not grow this without limit.
  if (parseCache.size > 1024) parseCache.clear()
  parseCache.set(src, node)
  return node
}

export interface EvaluateOptions {
  variables?: Record<string, unknown>
  resolveName?: (name: string) => unknown
  functions?: Record<string, (...a: unknown[]) => unknown>
  /** Allow a whole-column result (a per-repeat calculation). Default false. */
  allowVector?: boolean
}

/**
 * Evaluate one expression. The mirror of `expr_eval.evaluate`.
 *
 * `allowVector` decides what happens when the whole expression works out to a
 * `Vector`: false — the default and the right answer for every caller that
 * wants ONE value — refuses, because taking the first row would make it stand
 * silently for all of them.
 */
export function evaluate(expr: string, options: EvaluateOptions = {}): unknown {
  const src = String(expr ?? '').trim()
  if (!src) throw new ExprError('Empty expression.')
  if (src.length > MAX_LEN) throw new ExprError('Expression is too long.')

  const vars = options.variables || {}
  const funcs = options.functions || SAFE_FUNCS
  const node = compile(src)

  const walk = (n: Node, depth: number): unknown => {
    if (depth > MAX_DEPTH) throw new ExprError('Expression is too deeply nested.')
    switch (n.k) {
      case 'const':
        return n.v
      case 'list':
        return n.items.map((item) => walk(item, depth + 1))
      case 'name': {
        if (Object.prototype.hasOwnProperty.call(vars, n.v)) return vars[n.v]
        if (funcs[n.v]) throw new ExprError(`'${n.v}' is a function and needs ().`)
        if (options.resolveName) {
          const got = options.resolveName(n.v)
          if (got !== null && got !== undefined) return got
        }
        // An unknown bare identifier is a blank reference, so a not-yet-filled
        // column does not break the whole expression.
        return new PyInt(0)
      }
      case 'unary': {
        const a = walk(n.a, depth + 1)
        if (n.op === '+') return broadcast((v) => num(v), a)
        if (n.op === '-') return broadcast((v) => -num(v), a)
        return broadcast((v) => !truthy(v), a)
      }
      case 'bin': {
        const a = walk(n.a, depth + 1)
        const b = walk(n.b, depth + 1)
        switch (n.op) {
          case '+': return broadcast((x, y) => num(x) + num(y), a, b)
          case '-': return broadcast((x, y) => num(x) - num(y), a, b)
          case '*': return broadcast((x, y) => num(x) * num(y), a, b)
          case '/': return broadcast((x, y) => safeDiv(x, y), a, b)
          case '//': return broadcast((x, y) => safeFloorDiv(x, y), a, b)
          case '%': return broadcast((x, y) => safeMod(x, y), a, b)
          default: return broadcast((x, y) => num(x) ** num(y), a, b)
        }
      }
      case 'cmp': {
        const a = walk(n.a, depth + 1)
        const b = walk(n.b, depth + 1)
        return broadcast((x, y) => compare(n.op, x, y), a, b)
      }
      case 'in': {
        const a = walk(n.a, depth + 1)
        const items = new Set(n.items.map((it) => cmpStr(walk(it, depth + 1))))
        const hit = items.has(cmpStr(a))
        return n.neg ? !hit : hit
      }
      case 'bool': {
        const parts = n.parts.map((p) => walk(p, depth + 1))
        if (n.op === 'and') {
          return broadcast((...vs) => vs.every(truthy), ...parts)
        }
        return broadcast((...vs) => vs.some(truthy), ...parts)
      }
      case 'if': {
        const test = walk(n.test, depth + 1)
        if (!(test instanceof Vector)) {
          return truthy(test)
            ? walk(n.body, depth + 1)
            : walk(n.orelse, depth + 1)
        }
        return broadcast((c, x, y) => (truthy(c) ? x : y), test,
          walk(n.body, depth + 1), walk(n.orelse, depth + 1))
      }
      case 'call': {
        const fn = funcs[n.fn]
        if (!fn) throw new ExprError(`Unknown function '${n.fn}'.`)
        const args = n.args.map((a) => walk(a, depth + 1))
        try {
          if (AGGREGATE_FUNCS.has(n.fn)) return fn(...args)
          return broadcast(fn, ...args)
        } catch (exc) {
          if (exc instanceof ExprError) throw exc
          throw new ExprError(`${n.fn}() failed: ${(exc as Error).message}`)
        }
      }
      default:
        throw new ExprError('Expression is not allowed.')
    }
  }

  const result = walk(node, 0)
  if (result instanceof Vector && !options.allowVector) {
    throw new ExprError(
      'This works out to a whole column of a repeating form, which is many '
      + 'values. Reduce it — sum(…), avg(…), count(…), first(…) — or put the '
      + 'expression inside the repeat so it runs once per row.')
  }
  return unbox(result)
}

/** Unwrap the int box on the way out — callers see plain numbers. */
function unbox(v: unknown): unknown {
  if (v instanceof PyInt) return v.value
  if (v instanceof Vector) return Vector.of2([...v].map(unbox))
  return v
}

// ── references ──────────────────────────────────────────────────────────────

/** One `{…}` reference: `{A}`, `{A.B}` or `{A.B.C}` — the §6g path grammar. */
export const REFERENCE_RE =
  /\{\s*([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z0-9_]+){0,2})\s*\}/g

/**
 * Rewrite `{INVOICE.2.QTY} * {field.RATE}` into bindable variable names.
 *
 * The expression language has no attribute access and no subscripting by
 * design, so a dotted path is never PARSED by it — it is substituted for a
 * plain name first, exactly as `{form.X}` already is in the engine
 * (`_eval_expr_fill`). Naming them by order of first appearance makes the
 * rewritten text byte-identical across rows of a repeat, which is what lets the
 * parse cache hit on every row after the first.
 *
 * The mirror of `er.calc.bind_references`. The NAMES matter: both sides bind
 * the same variable to the same reference, so a parity fixture can state an
 * expression in path form and mean one thing.
 */
export function bindReferences(expression: string)
  : { src: string; refs: string[] } {
  const refs: string[] = []
  const src = String(expression ?? '').replace(REFERENCE_RE, (_m, ref: string) => {
    let at = refs.indexOf(ref)
    if (at < 0) { at = refs.length; refs.push(ref) }
    return `_r${at}`
  })
  return { src, refs }
}
